import React, { useId } from 'react';
import { twMerge } from 'tailwind-merge';

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  error?: string | null;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  /** Set false for inline/compact selects that shouldn't fill their container */
  fullWidth?: boolean;
}

const chevronSvg = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`;

const sizeClasses: Record<NonNullable<SelectProps['size']>, string> = {
  sm: 'h-9 px-3 pr-8 text-sm',
  md: 'h-11 px-3 pr-8 text-sm',
  lg: 'h-12 px-4 pr-10 text-base',
};

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, helperText, size = 'md', fullWidth = true, className = '', id, children, ...props }, ref) => {
    const autoId = useId();
    const selectId = id || autoId;
    const errorId = error ? `${selectId}-error` : undefined;
    const helperId = helperText ? `${selectId}-helper` : undefined;

    const describedBy = [errorId, helperId].filter(Boolean).join(' ') || undefined;

    return (
      <div className={twMerge('flex flex-col gap-1.5', fullWidth ? 'w-full' : '')}>
        {label && (
          <label
            htmlFor={selectId}
            className="text-sm font-medium text-text-secondary"
          >
            {label}
          </label>
        )}
        <select
          ref={ref}
          id={selectId}
          className={twMerge(
            'bg-surface border rounded-lg text-text-primary',
            'appearance-none cursor-pointer',
            'bg-no-repeat bg-[length:16px_16px]',
            'focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent',
            'disabled:cursor-not-allowed disabled:bg-bg-secondary disabled:opacity-60',
            'transition-colors shadow-none',
            error ? 'border-danger' : 'border-border-input',
            fullWidth ? 'w-full' : '',
            sizeClasses[size],
            className,
          )}
          style={{
            backgroundImage: chevronSvg,
            // Inline (not utility classes) so the chevron never tiles when the
            // bg-no-repeat/bg-[length] utilities aren't applied (e.g. public/marketing
            // layouts that load a different CSS chunk). See FeatureFormSection regression.
            backgroundRepeat: 'no-repeat',
            backgroundSize: '16px 16px',
            backgroundPosition: size === 'lg' ? 'right 12px center' : 'right 10px center',
            boxShadow: 'none',
          }}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          data-ui-select=""
          {...props}
        >
          {children}
        </select>
        {error && (
          <p id={errorId} className="text-xs text-danger" role="alert">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={helperId} className="text-xs text-text-tertiary">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
